import { useCallback, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/use-current-user";
import { sanitizeCoachInput } from "@/lib/coach-input";
import {
  MAX_COACH_INPUT_CHARS,
  useCoachObjective,
  type CoachError,
  type CoachMessage,
} from "@/hooks/use-coach";
import {
  CoachFeedbackCard,
  CoachFeedbackPlaceholder,
} from "./coach-feedback-card";
import { MAX_COACHING_ROUNDS, type WizardState } from "./wizard-state";
import { ReflectionDialog } from "./reflection-dialog";
import {
  logCoachSignals,
  resolveFamiliarityOverride,
  useCommitOnStreamEnd,
  useGraduation,
} from "./coach-helpers";

interface ObjectiveStepProps {
  state: WizardState;
  onChange: (next: WizardState) => void;
  onAdvance: () => void;
}

export function ObjectiveStep({ state, onChange, onAdvance }: ObjectiveStepProps) {
  const { feedback, isStreaming, error, signals, analyze, reset } =
    useCoachObjective();
  const { currentUser } = useCurrentUser();

  const { statement, history, roundCount, firstDraft, reflectionSubmitted } =
    state.objective;
  const { level, type, familiarity, focusAreaId, tacticId } = state.setup;

  const { reflectionOpen, triggerGraduation, finishReflection, skipReflection } =
    useGraduation({
      reflectionSubmitted,
      markReflectionSubmitted: () =>
        onChange({
          ...state,
          objective: { ...state.objective, reflectionSubmitted: true },
        }),
      onAdvance,
    });

  const displayRound = getDisplayRound(isStreaming, feedback.length > 0, roundCount);
  const capReached = roundCount >= MAX_COACHING_ROUNDS;
  const canRequest = isCoachingRequestable(isStreaming, capReached, level, type, statement);

  const requestCoaching = useCallback(async () => {
    if (!level || !type) return;
    const cleaned = sanitize(statement);
    if (!cleaned) return;

    // Capture first-draft snapshot once; feeds the reflection turn later.
    if (!firstDraft) {
      onChange({
        ...state,
        objective: { ...state.objective, firstDraft: cleaned },
      });
    }

    const overrideFamiliarity = resolveFamiliarityOverride(
      familiarity,
      currentUser?.familiarity,
    );

    await analyze({
      objective_statement: cleaned,
      level,
      type,
      prior_feedback: history,
      ...(overrideFamiliarity ? { familiarity: overrideFamiliarity } : {}),
      ...(focusAreaId ? { focus_area_id: focusAreaId } : {}),
      ...(tacticId ? { tactic_id: tacticId } : {}),
    });
  }, [
    analyze,
    currentUser?.familiarity,
    familiarity,
    firstDraft,
    focusAreaId,
    history,
    level,
    onChange,
    state,
    statement,
    tacticId,
    type,
  ]);

  const handleStreamCommit = useCallback(() => {
    if (isStreaming || !feedback || error) return;
    const cleaned = sanitize(statement);
    if (!cleaned) return;
    const alreadyCommitted =
      history.length > 0 &&
      history[history.length - 1]?.role === "assistant" &&
      history[history.length - 1]?.content === feedback;
    if (alreadyCommitted) return;

    const nextHistory: CoachMessage[] = [
      ...history,
      { role: "user", content: cleaned },
      { role: "assistant", content: feedback },
    ];
    onChange({
      ...state,
      objective: {
        ...state.objective,
        history: nextHistory,
        roundCount: roundCount + 1,
      },
    });

    logCoachSignals("objective", signals, roundCount + 1);
  }, [
    error,
    feedback,
    history,
    isStreaming,
    onChange,
    roundCount,
    signals,
    state,
    statement,
  ]);

  useCommitOnStreamEnd(isStreaming, error, feedback, handleStreamCommit);

  const handleStatementChange = (next: string) => {
    onChange({
      ...state,
      objective: {
        ...state.objective,
        statement: next.slice(0, MAX_COACH_INPUT_CHARS),
      },
    });
  };

  const handleRetry = () => {
    reset();
    void requestCoaching();
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Write Your Objective</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="objective-statement">Your Objective Statement</Label>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              A strong Objective is <span className="font-medium text-foreground">significant</span>,{" "}
              <span className="font-medium text-foreground">concrete</span>,{" "}
              <span className="font-medium text-foreground">action-oriented</span>, and{" "}
              <span className="font-medium text-foreground">inspiring</span>. Aim for 10 words or
              less, include a verb, and describe the destination &mdash; not the metrics.
            </p>
            <Textarea
              id="objective-statement"
              rows={6}
              maxLength={MAX_COACH_INPUT_CHARS}
              placeholder="e.g., Deliver a cross-system data inventory for every team lead by Q3"
              value={statement}
              onChange={(e) => handleStatementChange(e.target.value)}
              disabled={isStreaming}
            />
            <div className="flex justify-end text-xs text-[hsl(var(--muted-foreground))]">
              {statement.length} / {MAX_COACH_INPUT_CHARS}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={requestCoaching} disabled={!canRequest}>
              <Sparkles className="mr-2 h-4 w-4" />
              {roundCount === 0 ? "Get Coaching Feedback" : "Get Coaching Again"}
            </Button>
            {isStreaming && (
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                Coaching in progress...
              </span>
            )}
            {capReached && (
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                You&rsquo;ve iterated several times — consider saving and refining later.
              </span>
            )}
          </div>

          <ObjectiveReadinessCta
            readiness={signals.readiness}
            isStreaming={isStreaming}
            onAdvance={triggerGraduation}
          />
        </CardContent>
      </Card>

      <div>
        <CoachFeedbackSection
          isStreaming={isStreaming}
          feedback={feedback}
          error={error}
          displayRound={displayRound}
          onRetry={handleRetry}
        />
      </div>

      <ReflectionDialog
        open={reflectionOpen}
        entityType="objective"
        firstDraft={firstDraft || sanitize(statement) || ""}
        finalDraft={sanitize(statement) || ""}
        onSubmitted={finishReflection}
        onSkip={skipReflection}
      />
    </div>
  );
}

const sanitize = (raw: string) => sanitizeCoachInput(raw);

function isErrorRetryable(error: CoachError | null): boolean {
  if (!error) return false;
  return error.kind !== "aborted";
}

function CoachFeedbackSection({ isStreaming, feedback, error, displayRound, onRetry }: {
  isStreaming: boolean;
  feedback: string;
  error: CoachError | null;
  displayRound: number;
  onRetry: () => void;
}) {
  if (!isStreaming && feedback.length === 0 && !error) {
    return <CoachFeedbackPlaceholder />;
  }
  return (
    <CoachFeedbackCard
      feedback={feedback}
      roundNumber={displayRound}
      isStreaming={isStreaming}
      error={error}
      onRetry={isErrorRetryable(error) ? onRetry : undefined}
    />
  );
}

function isCoachingRequestable(
  isStreaming: boolean,
  capReached: boolean,
  level: string | null,
  type: string | null,
  statement: string,
): boolean {
  return !isStreaming && !capReached && level !== null && type !== null && sanitize(statement) !== null;
}

function getDisplayRound(isStreaming: boolean, _hasFeedback: boolean, roundCount: number): number {
  // During streaming: round is in progress, display as roundCount+1.
  // Post-commit: roundCount was already incremented on commit, so display as-is.
  return isStreaming ? roundCount + 1 : roundCount;
}

interface ObjectiveReadinessCtaProps {
  readiness: string | null | undefined;
  isStreaming: boolean;
  onAdvance: () => void;
}

function ObjectiveReadinessCta({ readiness, isStreaming, onAdvance }: ObjectiveReadinessCtaProps) {
  const [caveatsOpen, setCaveatsOpen] = useState(false);

  if (!readiness || readiness === "not_ready" || isStreaming) return null;

  if (readiness === "ready") {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <p className="font-semibold text-emerald-900">Your Objective looks ready.</p>
        <p className="mt-1 text-emerald-800">
          The coach flagged this draft as ready. You can move on to Key Results now, or keep iterating.
        </p>
        <Button
          size="sm"
          className="mt-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
          onClick={onAdvance}
        >
          Move to Key Results
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
        <p className="font-semibold text-amber-900">Ready with caveats.</p>
        <p className="mt-1 text-amber-800">
          The coach thinks you can move on but called out specific gaps. Review them, or continue with eyes open.
        </p>
        <Button size="sm" variant="outline" className="mt-2" onClick={() => setCaveatsOpen(true)}>
          Move to Key Results Anyway
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <Dialog open={caveatsOpen} onOpenChange={setCaveatsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Continue with open gaps?</DialogTitle>
            <DialogDescription>
              The coach flagged your Objective as ready with caveats. You can still refine it later. Continue to Key Results?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCaveatsOpen(false)}>
              Keep Iterating
            </Button>
            <Button onClick={() => { setCaveatsOpen(false); onAdvance(); }}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
